/**
 * Autoranging Gauge
 *
 * This gauge is configurable through both element attributes or options when
 * initiating.
 *
 * To set a value of a guage the .setValue(value) method can be called.
 *
 * Autoranging
 * The gauge autoranges and switches between 0-10, 0-100 and 0-1000 based on the given
 * value. When scaleMax is set and the value is smaller than the value of the autorange scale
 * value, the scaleMax value is used as the max i.e scaleMax:400 and the scale 0-1000 then the
 * scale becoms 0-400
 *
 * Options
 * rangeMarkerInterval: 1, // how many thick ones
 * rangeMarkers: 10, // how many steps
 * radius: 85, // radius of the gauge
 * digits: 0, // decimals
 * autoRange: true, // wether or not to use autoranging
 * value: 0 // gauge value
 * units: [] // array of units
 * valueDigits: 1 // determines the nr of decimals
 * scaleTarget: 100 // shows a red bar when crossed
 * scaleMin
 * scaleMax
 * scaleTarget
 *
 * Element
 * <div class=".js-energy-usage-gauge" data-autorange="true" data-digits="0" data-units="Wh,kWh,MWh,GWh,TWh,PWh" data-value="0"></></div>
 *
 * Initialize
 * this.chartUsage = new Gauge({ element: this.el.querySelector('.js-energy-usage-gauge') })
 *
 * -- or --
 *
 *  new Gauge({
 *    element: this.el.querySelector('.js-energy-usage-gauge'),
 *    autoRange: true,
 *    units: ['Wh', 'kWh', 'MWh', 'GWh', 'TWh', 'PWh'],
 *    value: 1100,
 *    scaleTarget: 3000
 *  })
 *

 */

var Marionette = require('marionette')
var App = require('app')
var _ = require('lodash')

var SvgJS = require('svgjs')
var convertUnits = require('./convert-units')

/*
<format> <div class="m-gauge js-gauge__pri-voltage" data-label="VAC" data-range-min="0" data-range-target="1000" data-range-max="1200" data-value="100"></div>
 */

var Gauge = Marionette.Object.extend({
  helpers: {
    polarToCartesian: function (centerX, centerY, radius, angleInDegrees) {
      var angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0

      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
      }
    },
    describeArc: function (x, y, radius, startAngle, endAngle) {
      var start = this.polarToCartesian(x, y, radius, endAngle)
      var end = this.polarToCartesian(x, y, radius, startAngle)

      var largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

      var d = [
        'M', start.x, start.y,
        'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y
      ].join(' ')

      return d
    },
    calculateAngle: function (value) {
      return (180 * (value / 100)) - 90
    },
    calculateProgress: function (value, min, max) {
      var total = max + parseFloat(Math.abs(min))
      return (value / total) * 100
    }
  },
  drawScale: function () {
    // empty the scale first so we can redraw the scale
    while (this.scaleGroup.node.lastChild) {
      this.scaleGroup.node.removeChild(this.scaleGroup.node.lastChild)
    }
    this.drawScaleArc()
  },
  drawScaleArc: function () {
    var self = this

    // draw scale
    var startAngle = self.helpers.calculateAngle(0)
    var endAngle = self.helpers.calculateAngle(100)

    var isTargetInRange = (self.scaleData.scaleTargetPercent < 99)

    // check if there is a target set
    if (!isNaN(self.scaleData.scaleTarget) && isTargetInRange) {
      endAngle = self.helpers.calculateAngle(self.scaleData.scaleTargetPercent)
    }
    self.scaleGroup.path(self.helpers.describeArc(self.options.cx, self.options.cy, self.options.radius, startAngle, endAngle)).fill('none').stroke({ width: 2, color: '#00B95B' })

    // draw red part
    if (!isNaN(self.scaleData.scaleTarget) && isTargetInRange) {
      self.scaleGroup.path(self.helpers.describeArc(self.options.cx, self.options.cy, self.options.radius, endAngle + 3, self.helpers.calculateAngle(100))).fill('none').stroke({ width: 2, color: '#D72B1F' })
    }

    //
    var counter = 0
    // draw the markers
    for (var i = 0; i <= self.options.rangeMarkers; i++) {
      var marker = {
        length: 7,
        thickness: 2
      }

      if (self.options.rangeMarkerInterval === counter) {
        marker.thickness = 1
        counter = -1
      } else {
        var digits = self.options.digits
        if (self.scaleData.scaleMax <= 1 && digits < 1) digits = 1
        self.drawScaleNumber(i, digits)
      }

      self.drawScaleMarker(i * (100 / self.options.rangeMarkers), marker)
      counter++
    }
  },
  drawScaleMarker: function (percent, lineConfig) {
    var self = this
    var color = '#00B95B'
    if (percent > self.scaleData.scaleTargetPercent && self.scaleData.scaleTargetPercent < 99) {
      color = '#D72B1F'
    }

    var angle = 180 * (percent / 100)
    var iAngle = angle - 180 // inverted angle

    // get point at
    var percentAlongPath = (angle / 180)

    var pos = Math.abs(self.mainArc.node.getTotalLength() - (self.mainArc.node.getTotalLength() * percentAlongPath))
    var point = self.mainArc.pointAt(pos)
    //
    var x = (Math.cos(iAngle * (Math.PI / 180)) * (self.options.radius + lineConfig.length)) + self.options.cx
    var y = (Math.sin(iAngle * (Math.PI / 180)) * (self.options.radius + lineConfig.length)) + self.options.cy

    self.scaleGroup.line(point.x, point.y, x, y).stroke({ width: lineConfig.thickness, color: color })
  },
  drawScaleNumber: function (position, digits) {
    var self = this
    var percent = (position / self.options.rangeMarkers) * 100
    var color = '#00B95B'

    if (percent > self.scaleData.scaleTargetPercent && self.scaleData.scaleTargetPercent < 99) {
      color = '#D72B1F'
    }
    var angle = 180 * (percent / 100)
    var iAngle = angle - 180 // inverted angle

    var value = ((self.scaleData.scaleMax - self.scaleData.scaleMin) / 100 * percent) + self.scaleData.scaleMin

    var x = Math.round((Math.cos(iAngle * (Math.PI / 180)) * (self.options.radius + 30)) + self.options.cx)
    var y = Math.round((Math.sin(iAngle * (Math.PI / 180)) * (self.options.radius + 20)) + self.options.cy)
    var formattedValue = value.toFixed(digits)

    var text = self.scaleGroup.text(formattedValue).move(x, y - 22)
    text.attr('alignment-baseline', 'middle')
    text.attr('text-anchor', 'middle')
    text.addClass('gauge__range-value')
    text.font({
      fill: color
    })
  },
  drawProgress: function () {
    var self = this

    var progressAngle = self.helpers.calculateAngle(0)
    this.progressLine = self.draw.path(self.helpers.describeArc(self.options.cx, self.options.cy, self.options.radius - 10, -90, progressAngle)).fill('none').stroke({ width: 13 })
  },
  setProgress: function (value) {
    var self = this

    var progress = self.helpers.calculateProgress(value - self.scaleData.scaleMin, self.scaleData.scaleMin, self.scaleData.scaleMax)
    if (progress > 100) progress = 100
    if (progress < 0) progress = 0
    var progressAngle = self.helpers.calculateAngle(progress)

    var color = '#00B95B'
    if (progress > self.scaleData.scaleTargetPercent) {
      color = '#D72B1F'
    }

    this.progressLine.plot(self.helpers.describeArc(self.options.cx, self.options.cy, self.options.radius - 12, -90, progressAngle))
    this.progressLine.stroke({color: color})
  },
  drawValue: function () {
    var self = this
    var color = '#00B95B'

    // text
    self.valueText = self.draw.text('0').move(self.options.cx, self.options.cy - 29)
    self.valueText.attr('alignment-baseline', 'middle')
    self.valueText.attr('text-anchor', 'middle')
    self.valueText.addClass('gauge__value')
    self.valueText.font({
      fill: color
    })

    self.labelText = self.draw.text(self.scaleData.label).move(self.options.cx, self.options.cy - 53)
    self.labelText.attr('alignment-baseline', 'middle')
    self.labelText.attr('text-anchor', 'middle')
    self.labelText.addClass('gauge__label')
    self.labelText.font({
      fill: color
    })
  },

  setValue: function (value, options) {
    var self = this
    var color = '#00B95B'
    self.options.value = value
    
    self.initializeScale()

    if (self.options.autoRange) {
      let autoRangeScale = convertUnits({value: value, units: self.options.units})
      value = autoRangeScale.value
      self.scaleData.label = autoRangeScale.unit || '--'
    }
    self.scaleData.value = value

    if (value === null) value = 0

    if (value >= self.scaleData.scaleTarget) color = '#D72B1F'

    self.valueText.text(value.toFixed(self.options.valueDigits))
    self.labelText.text(self.scaleData.label)

    self.valueText.font({fill: color})
    self.labelText.font({fill: color})

    // update the progress bar
    self.setProgress(value)
    self.drawScale()
  },
  /**
   * Calculate the scale data, this part is also responsible for the autoranging
   */
  initializeScale: function () {
    var self = this

    let elementData = {
      scaleMin: parseFloat(this.element.getAttribute('data-scale-min')),
      scaleMax: parseFloat(this.element.getAttribute('data-scale-max')),
      scaleTarget: parseFloat(this.element.getAttribute('data-scale-target'))
    }

    // populate options data
    let optionsData = {}
    if (self.options.hasOwnProperty('scaleMin')) optionsData['scaleMin'] = parseFloat(self.options.scaleMin)
    if (self.options.hasOwnProperty('scaleMax')) optionsData['scaleMax'] = parseFloat(self.options.scaleMax)
    if (self.options.hasOwnProperty('scaleTarget')) optionsData['scaleTarget'] = parseFloat(self.options.scaleTarget)

    self.scaleData = Object.assign(self.scaleData, elementData)

    var targetInRange = (self.scaleData.scaleTarget && (self.scaleData.scaleTarget <= self.scaleData.scaleMax)) ? true : false

    if (self.options.autoRange) {
      if (!self.options.units || self.options.units.length === 0) console.warn('Gauge autorange needs units...')
      // if autoranging
      let autoRangeScale = convertUnits({value: self.options.value, units: self.options.units})

      // if there is a max scale set check if the autorange scale max doesn't exceed the
      // defined max. if so use the defined max as max scale.
      var newScaleMax = autoRangeScale.max
      var definedScaleMax = autoRangeScale.factor * self.scaleData.scaleMax
      if (definedScaleMax < autoRangeScale.max && definedScaleMax > 0) newScaleMax = definedScaleMax

      self.scaleData = Object.assign(self.scaleData, {
        scaleMin: autoRangeScale.min,
        scaleMax: newScaleMax,
        label: autoRangeScale.unit,
        value: autoRangeScale.value
      })

      // scale target
      if (self.scaleData.hasOwnProperty('scaleTarget')) {
        // targetValue needs to be converted to the scale of th
        var convertedScaleTarget = autoRangeScale.factor * self.scaleData.scaleTarget
        self.scaleData.scaleTarget = convertedScaleTarget
      }
    }
    // calculate scalepercent
    if (self.scaleData.hasOwnProperty('scaleTarget')) {
      self.scaleData.scaleTargetPercent = (self.scaleData.scaleTarget / self.scaleData.scaleMax) * 100
    }
  },
  initialize: function (options) {
    var self = this
    // var config = App.config.maps
    var defaultOptions = {
      rangeMarkerInterval: 1,
      rangeMarkers: 10,
      radius: 85,
      digits: 0,
      valueDigits: 1,
      width: 270,
      height: 130
    }
    self.scaleData = {}
    self.element = options.element

    // prepare the data
    self.options = _.extend(defaultOptions, options)

    // options retrieved from data attributes
    self.elementOptions = {
      autoRange: (self.element.getAttribute('data-autorange') == 'true'),
      value: parseFloat(self.element.getAttribute('data-value')),
      scaleTarget: parseFloat(self.element.getAttribute('data-scaletarget')),
      label: self.element.getAttribute('data-label')
    }

    let elementUnits = self.element.getAttribute('data-units')
    if (elementUnits) self.elementOptions.units = elementUnits.split(',')

    //
    self.options = Object.assign(self.elementOptions, self.options)

    if (isNaN(self.options.value)) self.options.value = 0

    self.options['cx'] = self.options.width / 2
    self.options['cy'] = 120

    self.scaleData.value = self.options.value
    self.scaleData.label = self.options.label

    self.options.digits = parseInt(this.element.getAttribute('data-digits'))
    self.options.valueDigits = parseInt(this.element.getAttribute('data-value-digits')) || self.options.valueDigits

    self.initializeScale()
    // DRAW
    // draw the base
    self.draw = SvgJS(this.element).size('100%', '100%')
    self.viewbox = self.draw.viewbox(0, 0, self.options.width, self.options.height)
    self.scaleGroup = self.draw.group().size('100%', '100%')

    self.mainArc = self.draw.path(self.helpers.describeArc(self.options.cx, self.options.cy, self.options.radius, -90, 90)).fill('none').stroke('none')

    // fixed a weird jumping bug
    setTimeout(() => {
      self.drawProgress(self.helpers.calculateProgress(self.scaleData.value, self.scaleData.scaleMin, self.scaleData.scaleMax))
      self.drawValue()
      self.drawScale()
      self.setValue(self.options.value)
    }, 0)

  }
})

module.exports = Gauge



// WEBPACK FOOTER //
// ./js/common/gauge/index.js